import React, { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  FiUser,
  FiLock,
  FiMapPin,
  FiMusic,
  FiTag,
  FiSettings,
  FiChevronRight,
  FiLink,
  FiLogOut,
  FiHelpCircle,
  FiShield,
  FiFileText,
  FiTrash2,
  FiAlertTriangle,
  FiX,
} from 'react-icons/fi';
import { FiUsers } from 'react-icons/fi';
import { MdOutlineTune } from 'react-icons/md';
import { CreditCard } from 'lucide-react';
import { Header } from '../layout/Header';
import { BottomNav } from '../layout/BottomNav';
import { useAuth } from '../../hooks/useAuth';
import { useDeleteAccountMutation } from '../../api/auth';
import { Button } from '../ui/Button';

// ─── Types ─────────────────────────────────────────────────────────────────

interface SettingsCardProps {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  description?: string;
  to?: string;
  onClick?: () => void;
  danger?: boolean;
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function SectionLabel({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3 px-1">
      <span className="text-zinc-500 text-[11px] font-semibold uppercase tracking-widest">
        {title}
      </span>
      <div className="flex-1 h-px bg-white/5" />
    </div>
  );
}

function SettingsGroup({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-zinc-900/20 border border-white/5 rounded-3xl overflow-hidden divide-y divide-white/5">
      {children}
    </div>
  );
}

function GroupItem({
  icon,
  iconBg,
  title,
  description,
  to,
  onClick,
  danger = false,
}: SettingsCardProps) {
  const inner = (
    <div
      className={`flex items-center gap-4 px-5 py-4 transition-all duration-200 cursor-pointer group ${
        danger ? 'hover:bg-red-500/5' : 'hover:bg-white/3'
      }`}
      onClick={onClick}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className={`text-[14px] font-medium leading-tight ${
            danger ? 'text-red-400' : 'text-white'
          }`}
        >
          {title}
        </p>
        {description && (
          <p className="text-zinc-500 text-[12px] mt-0.5 leading-snug">{description}</p>
        )}
      </div>
      <FiChevronRight
        size={15}
        className={`shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 ${
          danger ? 'text-red-400/40' : 'text-zinc-700'
        }`}
      />
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="block">
        {inner}
      </Link>
    );
  }

  return inner;
}

// ─── Main Component ──────────────────────────────────────────────────────────

export const SettingsPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const deleteAccountMutation = useDeleteAccountMutation();

  const handleLogout = () => {
    logout();
    navigate({ to: '/sign-in' });
  };

  const handleDeleteAccount = () => {
    deleteAccountMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 mb-10 px-1 text-zinc-500 text-[12px] font-medium tracking-wide">
          <Link
            to="/profile"
            className="flex items-center gap-1.5 hover:text-white transition-all group/crumb"
          >
            <FiUser
              size={13}
              className="text-blue-500/80 group-hover/crumb:text-blue-400 transition-colors"
            />
            <span>Profile</span>
          </Link>
          <span className="text-zinc-800 font-light">/</span>
          <span className="text-zinc-200">Settings</span>
        </nav>

        {/* Page header */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <FiSettings size={20} className="text-blue-400" />
            </div>
            <h1 className="text-white font-bold text-[28px]">Settings</h1>
          </div>
          <p className="text-zinc-500 text-[14px] ml-1">
            Manage your account, preferences, and privacy.
          </p>
        </div>

        <div className="flex flex-col gap-8">
          {/* ── Account ───────────────────────────────────────────── */}
          <section>
            <SectionLabel title="Account" />
            <SettingsGroup>
              <GroupItem
                to="/profile/edit"
                icon={<FiUser size={17} className="text-blue-400" />}
                iconBg="bg-blue-500/10 border border-blue-500/20"
                title="Edit Profile"
                description="Update your name, bio, avatar, and social links"
              />
              <GroupItem
                to="/profile/change-password"
                icon={<FiLock size={17} className="text-purple-400" />}
                iconBg="bg-purple-500/10 border border-purple-500/20"
                title="Change Password"
                description="Update your account password securely"
              />
              <GroupItem
                to="/profile/preferred-locations"
                icon={<FiMapPin size={17} className="text-emerald-400" />}
                iconBg="bg-emerald-500/10 border border-emerald-500/20"
                title="Manage Preferred Locations"
                description="Choose cities where you discover events and artists"
              />
            </SettingsGroup>
          </section>

          {/* ── Music & Discovery ─────────────────────────────────── */}
          <section>
            <SectionLabel title="Music & Discovery" />
            <SettingsGroup>
              <GroupItem
                to="/profile/settings/genres"
                icon={<FiMusic size={17} className="text-pink-400" />}
                iconBg="bg-pink-500/10 border border-pink-500/20"
                title="Modify Genres"
                description="Customise your music genre preferences"
              />
              <GroupItem
                to="/profile/settings/tags"
                icon={<FiTag size={17} className="text-yellow-400" />}
                iconBg="bg-yellow-500/10 border border-yellow-500/20"
                title="Modify Tags"
                description="Add or remove tags that describe your style"
              />
              <GroupItem
                to="/profile/settings/user-type"
                icon={<MdOutlineTune size={19} className="text-orange-400" />}
                iconBg="bg-orange-500/10 border border-orange-500/20"
                title="Modify User Type"
                description="Change your artist, venue, or producer role"
              />
            </SettingsGroup>
          </section>

          {/* ── Payments ───────────────────────────────────────────── */}
          <section>
            <SectionLabel title="Payments" />
            <SettingsGroup>
              <GroupItem
                to="/profile/settings/payments"
                icon={<CreditCard size={17} className="text-green-400" />}
                iconBg="bg-green-500/10 border border-green-500/20"
                title="Payment Setup"
                description="Manage your Stripe account to receive payments"
              />
            </SettingsGroup>
          </section>

          {/* ── Audience ───────────────────────────────────────────── */}
          <section>
            <SectionLabel title="Audience" />
            <SettingsGroup>
              <GroupItem
                to="/profile/settings/subscriptions"
                icon={<FiUsers size={17} className="text-blue-400" />}
                iconBg="bg-blue-500/10 border border-blue-500/20"
                title="Profile Subscriptions"
                description="Manage subscriptions and mailing list settings"
              />
            </SettingsGroup>
          </section>

          {/* ── Sharing ───────────────────────────────────────────────── */}
          <section>
            <SectionLabel title="Sharing" />
            <SettingsGroup>
              <GroupItem
                to="/profile/settings/shareable-link"
                icon={<FiLink size={17} className="text-cyan-400" />}
                iconBg="bg-cyan-500/10 border border-cyan-500/20"
                title="Shareable Link"
                description="Share your socials and website in one link"
              />
            </SettingsGroup>
          </section>

          {/* ── More ──────────────────────────────────────────────── */}
          <section>
            <SectionLabel title="Legal & Support" />
            <SettingsGroup>
              <GroupItem
                to="/terms-of-service"
                icon={<FiFileText size={17} className="text-zinc-400" />}
                iconBg="bg-zinc-800 border border-white/8"
                title="Terms of Service"
                description="Our rules for using the platform"
              />
              <GroupItem
                to="/privacy-policy"
                icon={<FiShield size={17} className="text-zinc-400" />}
                iconBg="bg-zinc-800 border border-white/8"
                title="Privacy Policy"
                description="How we protect your data"
              />
              <GroupItem
                to="/community-guidelines"
                icon={<FiUsers size={17} className="text-zinc-400" />}
                iconBg="bg-zinc-800 border border-white/8"
                title="Community Guidelines"
                description="Our standards for behavior"
              />
              <GroupItem
                to="/help-support"
                icon={<FiHelpCircle size={17} className="text-zinc-400" />}
                iconBg="bg-zinc-800 border border-white/8"
                title="Help & Support"
                description="FAQs and contact options"
              />
            </SettingsGroup>
          </section>

          {/* ── Session management ───────────────────────────────────────── */}
          <section className="pb-4">
            <SectionLabel title="Session Management" />
            <SettingsGroup>
              <GroupItem
                icon={<FiLogOut size={17} className="text-red-400" />}
                iconBg="bg-red-500/10 border border-red-500/20"
                title="Log Out"
                description="Sign out of your Musicade account"
                danger
                onClick={handleLogout}
              />
              <GroupItem
                icon={<FiTrash2 size={17} className="text-red-500" />}
                iconBg="bg-red-500/10 border border-red-500/20"
                title="Delete Account"
                description="Permanently delete your account and all data"
                danger
                onClick={() => setShowDeleteModal(true)}
              />
            </SettingsGroup>
          </section>
        </div>
      </main>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => !deleteAccountMutation.isPending && setShowDeleteModal(false)}
          />
          <div className="relative w-full max-w-md bg-[#0c0c0c] border border-white/10 rounded-[32px] overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="p-8 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mb-6">
                <FiAlertTriangle className="text-red-500 w-8 h-8" />
              </div>

              <h3 className="text-white text-2xl font-bold mb-3">Delete Account?</h3>
              <p className="text-zinc-400 text-base leading-relaxed mb-8">
                This action is permanent and cannot be undone. All your profile data, events, and
                history will be lost forever.
              </p>

              <div className="flex flex-col gap-3 w-full">
                <Button
                  onClick={handleDeleteAccount}
                  disabled={deleteAccountMutation.isPending}
                  className="h-14 rounded-2xl bg-red-500 hover:bg-red-600 text-white font-bold text-base transition-all active:scale-95 disabled:opacity-50"
                >
                  {deleteAccountMutation.isPending ? 'Deleting...' : 'Yes, Delete Forever'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteAccountMutation.isPending}
                  className="h-14 rounded-2xl border-white/10 text-white hover:bg-white/5 font-bold text-base transition-all"
                >
                  Cancel
                </Button>
              </div>
            </div>
            {!deleteAccountMutation.isPending && (
              <button
                onClick={() => setShowDeleteModal(false)}
                className="absolute top-6 right-6 p-2 text-zinc-500 hover:text-white transition-colors rounded-full hover:bg-white/5"
              >
                <FiX size={20} />
              </button>
            )}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};
